import { crmService } from '../services/crmApi';

export type WebPushActivationResult = {
  supported: boolean;
  enabled: boolean;
  permission: NotificationPermission | 'unsupported';
  reason?: string;
};

function canUseWebPush() {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window;
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

export function getBrowserPushPermission(): NotificationPermission | 'unsupported' {
  if (!canUseWebPush()) {
    return 'unsupported';
  }

  return Notification.permission;
}

export function isWebPushNotificationSupported() {
  return canUseWebPush();
}

async function getServiceWorkerRegistration() {
  const registration = await navigator.serviceWorker.ready;
  if (!registration) {
    throw new Error('Service worker indisponivel para notificacoes.');
  }
  return registration;
}

export async function subscribeToWebPushNotifications(options: {
  requestPermission?: boolean;
} = {}): Promise<WebPushActivationResult> {
  if (!canUseWebPush()) {
    return {
      supported: false,
      enabled: false,
      permission: 'unsupported',
      reason: 'unsupported'
    };
  }

  const pushConfig = await crmService.getPushNotificationPublicKey();
  if (!pushConfig.enabled || !pushConfig.publicKey) {
    return {
      supported: true,
      enabled: false,
      permission: Notification.permission,
      reason: 'server_disabled'
    };
  }

  let permission = Notification.permission;
  if (permission === 'default' && options.requestPermission !== false) {
    permission = await Notification.requestPermission();
  }

  if (permission !== 'granted') {
    return {
      supported: true,
      enabled: false,
      permission,
      reason: 'permission_not_granted'
    };
  }

  const registration = await getServiceWorkerRegistration();
  const existingSubscription = await registration.pushManager.getSubscription();
  const subscription = existingSubscription || await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(pushConfig.publicKey)
  });

  await crmService.savePushNotificationSubscription(subscription.toJSON());

  return {
    supported: true,
    enabled: true,
    permission
  };
}

export async function syncWebPushSubscriptionSilently() {
  if (!canUseWebPush() || Notification.permission !== 'granted') {
    return;
  }

  try {
    await subscribeToWebPushNotifications({ requestPermission: false });
  } catch (error) {
    console.warn('Nao foi possivel sincronizar notificacoes push:', error);
  }
}

export async function unsubscribeFromWebPushNotifications() {
  if (!canUseWebPush()) {
    return;
  }

  const registration = await getServiceWorkerRegistration().catch(() => null);
  const subscription = registration ? await registration.pushManager.getSubscription() : null;
  const endpoint = subscription?.endpoint || '';

  if (endpoint) {
    await crmService.deletePushNotificationSubscription(endpoint).catch(() => {});
  } else {
    await crmService.deletePushNotificationSubscription('').catch(() => {});
  }

  if (subscription) {
    await subscription.unsubscribe().catch(() => false);
  }
}
